import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useSession } from '../api/session.js'
import { Icon } from '../components/icons.js'
import { t } from '../i18n/index.js'
import { addDays, formatDate, formatDateLong, formatWeekday, todayIso } from '../lib/format.js'
import { WorkerShell, useWorkerTasks, type OverlayTask } from './shell.js'

type SortKey = 'own' | 'address' | 'zip' | 'time'

/**
 * Tourenplanung im Browser.
 *
 * Whoever handles fifty flats in a day cannot plan that on a phone. This is
 * the same task list as the app, but as a table on a screen: sortable by
 * address, reorderable by hand, printable, exportable to Excel, and with one
 * link that opens the whole round in the navigation app. The order is kept in
 * this browser only, it is a planning aid, not a record.
 */
export function TourPage() {
  const { data: session } = useSession()
  const { tasks, today, byDay, isLoading } = useWorkerTasks()
  const [date, setDate] = useState(todayIso())
  const [sort, setSort] = useState<SortKey>('own')
  const [order, setOrder] = useState<string[]>([])
  const [dragging, setDragging] = useState<string | null>(null)

  const storageKey = `hauswerk.tour.${date}`
  useEffect(() => {
    try {
      setOrder(JSON.parse(localStorage.getItem(storageKey) ?? '[]') as string[])
    } catch {
      setOrder([])
    }
  }, [storageKey])

  const dayTasks = useMemo(() => {
    const list = (byDay.get(date) ?? []).slice()
    const own = new Map(order.map((id, index) => [id, index]))
    const address = (task: OverlayTask) => `${task.zip ?? ''} ${task.street ?? ''} ${task.label}`
    list.sort((a, b) => {
      if (sort === 'address') return address(a).localeCompare(address(b), 'de', { numeric: true })
      if (sort === 'zip') return (a.zip ?? '').localeCompare(b.zip ?? '') || address(a).localeCompare(address(b), 'de', { numeric: true })
      if (sort === 'time') return (a.dueTime ?? '99').localeCompare(b.dueTime ?? '99') || address(a).localeCompare(address(b), 'de', { numeric: true })
      const ia = own.get(a.id) ?? Number.MAX_SAFE_INTEGER
      const ib = own.get(b.id) ?? Number.MAX_SAFE_INTEGER
      return ia - ib || address(a).localeCompare(address(b), 'de', { numeric: true })
    })
    return list
  }, [byDay, date, order, sort])

  function persist(ids: string[]) {
    setOrder(ids)
    setSort('own')
    try {
      localStorage.setItem(storageKey, JSON.stringify(ids))
    } catch {
      /* planning aid only */
    }
  }

  function move(id: string, delta: number) {
    const ids = dayTasks.map((task) => task.id)
    const index = ids.indexOf(id)
    const target = index + delta
    if (index < 0 || target < 0 || target >= ids.length) return
    ids.splice(index, 1)
    ids.splice(target, 0, id)
    persist(ids)
  }

  function dropOn(targetId: string) {
    if (!dragging || dragging === targetId) return
    const ids = dayTasks.map((task) => task.id)
    const from = ids.indexOf(dragging)
    const to = ids.indexOf(targetId)
    ids.splice(from, 1)
    ids.splice(to, 0, dragging)
    persist(ids)
    setDragging(null)
  }

  const open = dayTasks.filter((task) => task.status !== 'done')
  const stops = [...new Map(open.map((task) => [addressOf(task), task])).values()]
  const routeUrl =
    stops.length > 0
      ? `https://www.google.com/maps/dir/${stops.map((task) => encodeURIComponent(addressOf(task))).join('/')}`
      : null

  function exportCsv() {
    const rows = [
      ['Reihenfolge', 'Uhrzeit', 'Liegenschaft', 'Objekt', 'Adresse', 'Leistung', 'Youtilly-ID', 'Hinweis', 'Status'],
      ...dayTasks.map((task, index) => [
        String(index + 1),
        task.dueTime ?? '',
        task.siteName,
        task.path.join(' › '),
        addressOf(task),
        task.title,
        task.externalRef ?? '',
        task.note ?? '',
        task.status === 'done' ? 'erledigt' : 'offen',
      ]),
    ]
    const csv = `﻿${rows.map((row) => row.map((cell) => (/[";\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(';')).join('\r\n')}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `Tour_${date}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  if (session && !session.user.planningAccess) {
    return (
      <WorkerShell title={t('tour.title')} back="/app">
        <p className="muted" style={{ fontSize: 17 }}>
          {t('tour.not_enabled')}
        </p>
      </WorkerShell>
    )
  }

  const days = Array.from({ length: 15 }, (_, index) => addDays(today, index - 1))

  return (
    <div className="wk" style={{ maxWidth: 'none', fontSize: 15 }}>
      <header className="wk-top">
        <div>
          <Link to="/app" style={{ display: 'block', marginBottom: 2 }}>
            ← {t('app.back')}
          </Link>
          <h1>{t('tour.title')}</h1>
          <div className="date">{formatDateLong(date)}</div>
        </div>
      </header>
      <div className="tour">
        <div className="tour-head">
          <label className="field" style={{ marginBottom: 0 }}>
            <span>{t('tour.day')}</span>
            <select value={date} onChange={(event) => setDate(event.target.value)}>
              {days.map((day) => (
                <option key={day} value={day}>
                  {day === today ? t('day.today') : formatWeekday(day, 'long')}, {formatDate(day)} ({t('tour.tasks_count', { count: (byDay.get(day) ?? []).length })})
                </option>
              ))}
            </select>
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>{t('tour.sort')}</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
              <option value="own">{t('tour.sort_own')}</option>
              <option value="address">{t('tour.sort_address')}</option>
              <option value="zip">{t('tour.sort_zip')}</option>
              <option value="time">{t('tour.sort_time')}</option>
            </select>
          </label>
          <div className="grow" />
          <div className="actions">
            {routeUrl && (
              <a className="btn secondary" href={routeUrl} target="_blank" rel="noreferrer">
                <Icon name="route" /> {t('tour.route', { count: stops.length })}
              </a>
            )}
            <button type="button" className="btn secondary" onClick={exportCsv} disabled={dayTasks.length === 0}>
              <Icon name="table" /> {t('tour.export')}
            </button>
            <button type="button" className="btn secondary" onClick={() => window.print()} disabled={dayTasks.length === 0}>
              <Icon name="print" /> {t('tour.print')}
            </button>
          </div>
        </div>

        {isLoading && tasks.length === 0 ? (
          <div className="skeleton" style={{ height: 200 }} />
        ) : dayTasks.length === 0 ? (
          <div className="card">
            <div className="empty">
              <strong>{t('tour.nothing')}</strong>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-head">
              <h2>
                {t('tour.summary', { open: open.length, done: dayTasks.length - open.length })}
              </h2>
              <span className="muted small">{t('tour.reorder_hint')}</span>
            </div>
            {/* Phone: the same list as cards; the table below stays for the screen. */}
            <div className="tour-cards">
              {dayTasks.map((task, index) => (
                <div key={task.id} className={`tour-card ${task.status === 'done' ? 'done' : ''}`}>
                  <div className="no">{index + 1}</div>
                  <div className="what">
                    <div className="t">{task.street ?? task.siteName}</div>
                    <div className="m">
                      {task.path.join(' › ') || task.siteName}, {task.title}
                      {task.dueTime ? `, ${t('card.until', { time: task.dueTime })}` : ''}
                    </div>
                    {task.note && <div className="m">{task.note}</div>}
                  </div>
                  <div className="side">
                    <span className="order-btns" style={{ flexDirection: 'row' }}>
                      <button type="button" aria-label={t('tour.up')} disabled={index === 0} onClick={() => move(task.id, -1)}>
                        ▲
                      </button>
                      <button type="button" aria-label={t('tour.down')} disabled={index === dayTasks.length - 1} onClick={() => move(task.id, 1)}>
                        ▼
                      </button>
                    </span>
                    {task.status === 'done' ? (
                      <span className="badge done">{t('card.done')}</span>
                    ) : (
                      <Link className="btn secondary small" to={`/app/aufgabe/${task.id}?zurueck=tour`}>
                        {t('tour.open')}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="table-scroll">
              <table className="data">
                <thead>
                  <tr>
                    <th className="order">{t('tour.col_no')}</th>
                    <th>{t('tour.col_address')}</th>
                    <th>{t('tour.col_object')}</th>
                    <th>{t('tour.col_task')}</th>
                    <th className="nowrap">{t('tour.col_time')}</th>
                    <th>{t('tour.col_note')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {dayTasks.map((task, index) => (
                    <tr
                      key={task.id}
                      className={`${task.status === 'done' ? 'done' : ''} ${dragging === task.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={() => setDragging(task.id)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => dropOn(task.id)}
                      onDragEnd={() => setDragging(null)}
                    >
                      <td className="order">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                          <strong>{index + 1}</strong>
                          <span className="order-btns">
                            <button type="button" aria-label={t('tour.up')} disabled={index === 0} onClick={() => move(task.id, -1)}>
                              ▲
                            </button>
                            <button type="button" aria-label={t('tour.down')} disabled={index === dayTasks.length - 1} onClick={() => move(task.id, 1)}>
                              ▼
                            </button>
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="strong">{task.street ?? task.siteName}</div>
                        <div className="muted small">{[task.zip, task.city].filter(Boolean).join(' ')}</div>
                      </td>
                      <td>
                        {task.path.join(' › ') || task.siteName}
                        {task.externalRef && <div className="muted small">{task.externalRef}</div>}
                      </td>
                      <td>{task.title}</td>
                      <td className="nowrap">{task.dueTime ?? '–'}</td>
                      <td className="small">{task.note ?? ''}</td>
                      <td className="nowrap" style={{ textAlign: 'right' }}>
                        {task.status === 'done' ? (
                          <span className="badge done">{t('card.done')}</span>
                        ) : (
                          <Link className="btn secondary small" to={`/app/aufgabe/${task.id}?zurueck=tour`}>
                            {t('tour.open')}
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function addressOf(task: OverlayTask): string {
  return [task.street, [task.zip, task.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || task.siteName
}
